import React, { useState, useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  makeStyles,
  Container,
  CardHeader,
  CardContent,
  Divider,
  MenuItem,
  Grid,
  InputLabel,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useAlert } from 'react-alert';
import * as Yup from "yup";
import { Formik } from "formik";
import Page from 'src/components/Page';
import PageHeader from 'src/views/Common/PageHeader';
import services from '../Services';
import { useNavigate } from 'react-router-dom';
import { trackPromise } from 'react-promise-tracker';
import { LoadingComponent } from '../../../utils/newLoader';
import authService from '../../../utils/permissionAuth';
import tokenService from '../../../utils/tokenDecoder';
import { AlertDialogWithoutButton } from '../../Common/AlertDialogWithoutButton';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHead: {
    backgroundColor: '#F5F5F5',
  },
  tableHeadCell: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#333',
    borderBottom: '2px solid #e0e0e0',
    padding: '10px 16px',
  },
  routeCell: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#1a1a2e',
    borderRight: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
    verticalAlign: 'middle',
    padding: '10px 16px',
  },
  dataCell: {
    fontSize: '0.875rem',
    padding: '8px 16px',
    borderBottom: '1px solid #f0f0f0',
    textAlign: 'center',
  },
  lastTierCell: {
    borderBottom: '2px solid #e0e0e0',
  },
  noDataText: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  editButton: {
    padding: 4,
    color: theme.palette.primary.main,
  },
  deleteButton: {
    padding: 4,
    color: theme.palette.error.main,
  },
}));

var screenCode = "INCENTIVECONFIGURATION";

export default function IncentiveConfigurationListing() {
  const classes = useStyles();
  const alert = useAlert();
  const [routeData, setRouteData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [groups, setGroups] = useState();
  const [factories, setFactories] = useState();
  const [routes, setRoutes] = useState();
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [routeList, setRouteList] = useState({
    groupID: '0',
    factoryID: '0',
    routeID: '0'
  });
  const [dialogbox, setDialogbox] = useState(false);
  const [selectedIncentiveConfigurationID, setSelectedIncentiveConfigurationID] = useState(null);
  const navigate = useNavigate();
  let encrypted = "";

  const handleClick = () => {
    encrypted = btoa('0');
    navigate('/app/IncentiveConfiguration/addEdit/' + encrypted);
  };

  const handleClickEdit = (incentiveConfigurationID) => {
    encrypted = btoa(incentiveConfigurationID.toString());
    navigate('/app/IncentiveConfiguration/addedit/' + encrypted);
  };

  const handleClickDelete = (incentiveConfigurationID) => {
    setSelectedIncentiveConfigurationID(incentiveConfigurationID);
    setDialogbox(true);
  };

  const confirmDelete = async () => {
    setDialogbox(false);
    if (selectedIncentiveConfigurationID) {
      const response = await services.deleteConfiguration(selectedIncentiveConfigurationID);
      if (response.statusCode === 'Success') {
        alert.success(response.message);
        setRouteData(prev => prev.filter(row => row.incentiveConfigurationID !== selectedIncentiveConfigurationID));
      } else {
        alert.error(response.message);
      }
    }
    setSelectedIncentiveConfigurationID(null);
  };

  const cancelDelete = () => {
    setDialogbox(false);
    setSelectedIncentiveConfigurationID(null);
  };

  const [permissionList, setPermissions] = useState({
    isGroupFilterEnabled: false,
    isFactoryFilterEnabled: false
  });

  useEffect(() => {
    getPermissions();
    getGroupsForDropdown();
  }, []);

  useEffect(() => {
    if (routeList.groupID !== '0') {
      trackPromise(getFactoriesByGroupID());
    }
  }, [routeList.groupID]);

  useEffect(() => {
    if (routeList.groupID !== '0' && routeList.factoryID !== '0') {
      trackPromise(getRoutesByFactoryID());
      const model = {
        groupID: parseInt(routeList.groupID),
        factoryID: parseInt(routeList.factoryID),
        routeID: parseInt(routeList.routeID) || 0
      };
      setIsSearchClicked(true);
      trackPromise(GetIncentiveConfigurationDetails(model));
    }
  }, [routeList.groupID, routeList.factoryID]);

  useEffect(() => {
    if (!routeData || routeData.length === 0) {
      setGroupedData([]);
      return;
    }
    const map = {};
    const order = [];
    routeData.forEach(row => {
      const key = row.routeID;
      if (!map[key]) {
        map[key] = {
          routeID: row.routeID,
          routeName: row.routeName,
          tiers: [],
        };
        order.push(key);
      }
      map[key].tiers.push({
        incentiveConfigurationID: row.incentiveConfigurationID,
        cropWeight: row.cropWeight,
        amountPerKilo: row.amountPerKilo,
      });
    });
    setGroupedData(order.map(k => map[k]));
  }, [routeData]);

  function getPermissions() {
    authService.getPermissionsByScreen(screenCode)
      .then(permissions => {
        const isAuthorized = permissions.find(p => p.permissionCode === 'VIEWINCENTIVECONFIGURATION');
        if (isAuthorized === undefined) {
          navigate('/404');
        }
        const isGroupFilterEnabled = permissions.find(p => p.permissionCode === 'GROUPDROPDOWN');
        const isFactoryFilterEnabled = permissions.find(p => p.permissionCode === 'FACTORYDROPDOWN');
        setPermissions({
          ...permissionList,
          isGroupFilterEnabled: isGroupFilterEnabled !== undefined,
          isFactoryFilterEnabled: isFactoryFilterEnabled !== undefined,
        });
        setRouteList({
          ...routeList,
          groupID: parseInt(tokenService.getGroupIDFromToken()),
          factoryID: parseInt(tokenService.getFactoryIDFromToken())
        });
      })
      .catch(error => {
        console.error('Error fetching permissions:', error);
      });
  }

  async function getFactoriesByGroupID() {
    const fac = await services.getFactoriesByGroupID(routeList.groupID);
    setFactories(fac);
  }

  async function getRoutesByFactoryID() {
    const routes = await services.getRoutesByGroupIDFactoryID(routeList.groupID, routeList.factoryID);
    setRoutes(routes);
  }

  async function getGroupsForDropdown() {
    const groups = await services.getGroupsForDropdown();
    setGroups(groups);
  }

  function generateDropDownMenu(data) {
    let items = [];
    if (data != null) {
      for (const [key, value] of Object.entries(data)) {
        items.push(<MenuItem key={key} value={key}>{value}</MenuItem>);
      }
    }
    return items;
  }

  function handleChange(e) {
    const target = e.target;
    const name = target.name;
    let value = target.value;
    setRouteList({ ...routeList, [name]: value });
    if (name === 'groupID' || name === 'factoryID') {
      setRouteData([]);
      setIsSearchClicked(false);
    }
  }

  const handleClickSearch = () => {
    setIsSearchClicked(true);
    const model = {
      groupID: parseInt(routeList.groupID),
      factoryID: parseInt(routeList.factoryID),
      routeID: parseInt(routeList.routeID) || 0
    };
    trackPromise(GetIncentiveConfigurationDetails(model));
  };

  const handleRouteFilterChange = (e) => {
    const value = e.target.value;
    setRouteList(prev => ({ ...prev, routeID: value }));
    if (routeList.groupID !== '0' && routeList.factoryID !== '0') {
      setIsSearchClicked(true);
      const model = {
        groupID: parseInt(routeList.groupID),
        factoryID: parseInt(routeList.factoryID),
        routeID: parseInt(value) || 0
      };
      trackPromise(GetIncentiveConfigurationDetails(model));
    }
  };

  async function GetIncentiveConfigurationDetails(model) {
    const response = await services.GetIncentiveConfigurationDetails(model);
    if (response.statusCode === "Success" && response.data) {
      const routeDataArray = Array.isArray(response.data) ? response.data : [response.data];
      setRouteData(routeDataArray);
    } else {
      setRouteData([]);
    }
  }

  function cardTitle(titleName) {
    return (
      <Grid container spacing={1}>
        <Grid item md={10} xs={12}>
          {titleName}
        </Grid>
        <Grid item md={2} xs={12}>
          <PageHeader
            onClick={handleClick}
            isEdit={true}
            toolTiptitle={"Add Incentive Configuration"}
          />
        </Grid>
      </Grid>
    );
  }

  function renderGroupedTable() {
    if (groupedData.length === 0) {
      return (
        <Typography className={classes.noDataText}>
          No incentive configuration records found.
        </Typography>
      );
    }

    const rows = [];
    groupedData.forEach((route) => {
      const tierCount = route.tiers.length;

      route.tiers.forEach((tier, tierIndex) => {
        const isLastTier = tierIndex === tierCount - 1;

        rows.push(
          <TableRow key={`${route.routeID}-${tier.incentiveConfigurationID}`}>

            {tierIndex === 0 && (
              <TableCell
                rowSpan={tierCount}
                className={classes.routeCell}
                style={{ borderBottom: '2px solid #e0e0e0', verticalAlign: 'top', paddingTop: 10 }}
              >
                {route.routeName}
              </TableCell>
            )}
            <TableCell
              className={`${classes.dataCell} ${isLastTier ? classes.lastTierCell : ''}`}
            >
              {tierIndex + 1}
            </TableCell>
            <TableCell
              className={`${classes.dataCell} ${isLastTier ? classes.lastTierCell : ''}`}
            >
              {parseFloat(tier.cropWeight).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </TableCell>
            <TableCell
              className={`${classes.dataCell} ${isLastTier ? classes.lastTierCell : ''}`}
            >
              {parseFloat(tier.amountPerKilo).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </TableCell>
            <TableCell
              className={`${classes.dataCell} ${isLastTier ? classes.lastTierCell : ''}`}
            >
              <Tooltip title="Edit">
                <IconButton
                  className={classes.editButton}
                  size="small"
                  onClick={() => handleClickEdit(tier.incentiveConfigurationID)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  className={classes.deleteButton}
                  size="small"
                  onClick={() => handleClickDelete(tier.incentiveConfigurationID)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        );
      });
    });

    return (
      <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
        <Table size="small" aria-label="incentive configuration grouped table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell} style={{ width: '25%' }}>
                Route
              </TableCell>
              <TableCell className={classes.tableHeadCell} style={{ width: '8%', textAlign: 'center' }}>
                #
              </TableCell>
              <TableCell className={classes.tableHeadCell} style={{ width: '25%', textAlign: 'center' }}>
                Apply Incentive Above (kg)
              </TableCell>
              <TableCell className={classes.tableHeadCell} style={{ width: '22%', textAlign: 'center' }}>
                Incentive Rate (Rs.)
              </TableCell>
              <TableCell className={classes.tableHeadCell} style={{ width: '20%', textAlign: 'center' }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Page className={classes.root} title="Incentive Configuration">
      <LoadingComponent />
      <Container maxWidth={false}>
        <Formik
          initialValues={{
            groupID: routeList.groupID,
            factoryID: routeList.factoryID,
            routeID: routeList.routeID,
          }}
          validationSchema={
            Yup.object().shape({
              groupID: Yup.number().required('Group required').min(1, 'Group required'),
              factoryID: Yup.number().required('Factory required').min(1, 'Factory required'),
            })
          }
          onSubmit={handleClickSearch}
          enableReinitialize
        >
          {({
            errors,
            handleBlur,
            handleSubmit,
            touched,
            values,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box mt={0}>
                <Card>
                  <CardHeader title={cardTitle("Incentive Configuration")} />
                  <PerfectScrollbar>
                    <Divider />
                    <CardContent style={{ marginBottom: "2rem" }}>
                      <Grid container spacing={3}>
                        <Grid item md={3} xs={12}>
                          <InputLabel shrink id="groupID">
                            Group *
                          </InputLabel>
                          <TextField
                            select
                            fullWidth
                            name="groupID"
                            onChange={(e) => {
                              handleChange(e);
                              setRouteList((prev) => ({
                                ...prev,
                                groupID: e.target.value,
                                factoryID: '0',
                                routeID: '0',
                              }));
                            }}
                            size="small"
                            value={values.groupID}
                            variant="outlined"
                            error={Boolean(touched.groupID && errors.groupID)}
                            helperText={touched.groupID && errors.groupID}
                            InputProps={{ readOnly: !permissionList.isGroupFilterEnabled }}
                          >
                            <MenuItem value="0">--Select Group--</MenuItem>
                            {generateDropDownMenu(groups)}
                          </TextField>
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <InputLabel shrink id="factoryID">
                            Factory *
                          </InputLabel>
                          <TextField
                            select
                            fullWidth
                            name="factoryID"
                            onChange={(e) => {
                              handleChange(e);
                              setRouteList((prev) => ({
                                ...prev,
                                factoryID: e.target.value,
                                routeID: '0',
                              }));
                            }}
                            size="small"
                            value={values.factoryID}
                            variant="outlined"
                            error={Boolean(touched.factoryID && errors.factoryID)}
                            helperText={touched.factoryID && errors.factoryID}
                            InputProps={{ readOnly: !permissionList.isFactoryFilterEnabled }}
                          >
                            <MenuItem value="0">--Select Factory--</MenuItem>
                            {generateDropDownMenu(factories)}
                          </TextField>
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <InputLabel shrink id="routeID">
                            Route
                          </InputLabel>
                          <TextField
                            select
                            fullWidth
                            name="routeID"
                            size="small"
                            onChange={handleRouteFilterChange}
                            value={values.routeID}
                            variant="outlined"
                            InputProps={{ readOnly: false }}
                          >
                             <MenuItem value="0">--Select Routes--</MenuItem>
                            {generateDropDownMenu(routes)}
                          </TextField>
                        </Grid>
                        <Grid item md={3} xs={12}>
                          <Box display="flex" justifyContent="flex-end" p={2}>
                            <Button color="primary" type="submit" variant="contained">
                              Search
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                    {isSearchClicked && (
                      <Box px={2} pb={2}>
                        {renderGroupedTable()}
                      </Box>
                    )}
                  </PerfectScrollbar>
                </Card>
              </Box>
            </form>
          )}
        </Formik>
      </Container>
      {dialogbox ? (
        <AlertDialogWithoutButton
          confirmData={confirmDelete}
          cancelData={cancelDelete}
          headerMessage="Confirm Delete"
          discription="Are you sure you want to delete this incentive tier?"
        />
      ) : null}
    </Page>
  );
}
